'use client'

import { ImportDataModal } from '@/components/import/ImportDataModal'
import { Professional } from '@/lib/api/common/interfaces/professional.interface'
import { getProfessionalsByOrganization } from '@/lib/api/professionals/professionals-api'
import { ShareableModule } from '@/lib/api/share/share-api'
import { formatPhoneNumber } from '@/lib/form-state/util'
import { useDebounce } from '@/lib/hooks/use-debounce'
import { usePaginatedPage } from '@/lib/hooks/use-paginated-page'
import { useUrlPage } from '@/lib/hooks/use-url-page'
import PaginationControls from '@/components/pagination/PaginationControls'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useCallback, useMemo, useState } from 'react'
import { FaUserDoctor } from 'react-icons/fa6'
import {
    MdAdd,
    MdArrowForward,
    MdClose,
    MdEmail,
    MdFileDownload,
    MdHandshake,
    MdLocationOn,
    MdMedicalServices,
    MdPhone,
    MdSearch,
    MdWork,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'

const getAvatarColor = (name: string): string => {
    const colors = [
        'from-blue-500 to-blue-600',
        'from-purple-500 to-purple-600',
        'from-pink-500 to-pink-600',
        'from-rose-500 to-rose-600',
        'from-orange-500 to-orange-600',
        'from-amber-500 to-amber-600',
        'from-emerald-500 to-emerald-600',
        'from-teal-500 to-teal-600',
        'from-cyan-500 to-cyan-600',
        'from-indigo-500 to-indigo-600',
        'from-violet-500 to-violet-600',
        'from-fuchsia-500 to-fuchsia-600',
        'from-sky-500 to-sky-600',
        'from-lime-500 to-lime-600',
        'from-green-500 to-green-600',
    ]
    let hash = 0
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash)
    }
    const index = Math.abs(hash) % colors.length
    return colors[index]
}

export default function ProfessionalsPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { canCreate } = useResourcePermissions('person', 'professionals')
    const [showSearch, setShowSearch] = useState(false)
    const [searchInput, setSearchInput] = useState('')
    const [showImportModal, setShowImportModal] = useState(false)

    const debouncedSearch = useDebounce(searchInput, 300)
    const organizationId = user?.organizationId

    const fetchFn = useCallback(
        (params: { page: number; limit: number; search?: string }) =>
            getProfessionalsByOrganization(organizationId!, params),
        [organizationId]
    )

    const filters = useMemo(
        () => ({ search: debouncedSearch.trim() || undefined }),
        [debouncedSearch]
    )

    const [urlPage, setUrlPage] = useUrlPage()

    const {
        items: professionals,
        loading,
        page,
        limit,
        total,
        setPage,
        reload,
    } = usePaginatedPage<Professional, { search?: string }>({
        fetchFn,
        filters,
        limit: 15,
        enabled: !!organizationId,
        page: urlPage,
        onPageChange: setUrlPage,
    })

    const handleCancelSearch = () => {
        setShowSearch(false)
        setSearchInput('')
    }

    if (loading && professionals.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando profissionais...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-4 md:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-secondary via-secondary/90 to-secondary/70 flex items-center justify-center shadow-lg shadow-secondary/20">
                        <MdWork className="text-3xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-base-content">
                            Profissionais
                        </h1>
                        <p className="text-sm md:text-base text-base-content/60 mt-1">
                            {total}{' '}
                            {total === 1 ? 'profissional' : 'profissionais'}{' '}
                            cadastrado{total !== 1 ? 's' : ''}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {showSearch ? (
                        <div className="flex items-center gap-2 w-full sm:w-auto">
                            <div className="relative flex-1 sm:flex-initial">
                                <input
                                    type="text"
                                    placeholder="Buscar profissionais..."
                                    value={searchInput}
                                    onChange={e =>
                                        setSearchInput(e.target.value)
                                    }
                                    className="input input-bordered w-full sm:w-72 pl-10"
                                    autoFocus
                                />
                                <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/40 text-lg" />
                            </div>
                            <button
                                type="button"
                                onClick={handleCancelSearch}
                                className="btn btn-ghost gap-2"
                            >
                                <MdClose className="text-lg" />
                                <span className="hidden sm:inline">
                                    Cancelar
                                </span>
                            </button>
                        </div>
                    ) : (
                        <>
                            <button
                                onClick={() => setShowSearch(true)}
                                className="btn btn-ghost gap-2"
                            >
                                <MdSearch className="text-lg" />
                                <span className="hidden sm:inline">Buscar</span>
                            </button>
                            {canCreate && (
                                <>
                                    <button
                                        className="btn btn-outline btn-secondary gap-2"
                                        onClick={() => setShowImportModal(true)}
                                    >
                                        <MdFileDownload className="text-xl" />
                                        <span className="hidden sm:inline">
                                            Importar
                                        </span>
                                    </button>
                                    <button
                                        onClick={() =>
                                            navigate(
                                                '/person/professionals/new'
                                            )
                                        }
                                        className="btn btn-secondary gap-2 shadow-lg shadow-secondary/20"
                                    >
                                        <MdAdd className="text-lg" />
                                        <span className="hidden sm:inline">
                                            Adicionar
                                        </span>
                                    </button>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>

            {professionals.length === 0 ? (
                <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-2xl border border-base-300/50 shadow-lg p-16 text-center">
                    <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-secondary/10 to-secondary/5 flex items-center justify-center">
                        <MdWork className="text-5xl text-secondary/40" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-base-content mb-3">
                        Nenhum profissional encontrado
                    </h3>
                    <p className="text-base-content/60 mb-8 max-w-md mx-auto">
                        {debouncedSearch
                            ? 'Tente buscar com outros termos ou limpe a busca para ver todos os profissionais.'
                            : 'Comece adicionando seu primeiro profissional ao sistema.'}
                    </p>
                    {!debouncedSearch && canCreate && (
                        <button
                            onClick={() =>
                                navigate('/person/professionals/new')
                            }
                            className="btn btn-secondary btn-lg gap-2 shadow-lg shadow-secondary/20"
                        >
                            <MdAdd className="text-xl" />
                            Adicionar Profissional
                        </button>
                    )}
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
                        {professionals.map(professional => (
                            <div
                                key={professional.id}
                                onClick={() =>
                                    navigate(
                                        `/person/professionals/${professional.id}`
                                    )
                                }
                                className="group bg-gradient-to-br from-base-100 to-base-200/20 rounded-2xl border border-base-300/50 shadow-sm hover:shadow-xl hover:border-secondary/30 transition-all duration-300 cursor-pointer overflow-hidden relative"
                            >
                                <div className="absolute inset-0 bg-gradient-to-br from-secondary/0 to-secondary/0 group-hover:from-secondary/5 group-hover:to-transparent transition-all duration-300 pointer-events-none" />

                                <div className="relative p-6">
                                    <div className="flex items-start gap-4 mb-5">
                                        {professional.photoUrl ? (
                                            <img
                                                src={professional.photoUrl}
                                                alt={professional.name}
                                                className="w-16 h-16 rounded-2xl object-cover shadow-lg flex-shrink-0"
                                            />
                                        ) : (
                                            <div
                                                className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${getAvatarColor(professional.name)} flex items-center justify-center shadow-lg flex-shrink-0`}
                                            >
                                                <FaUserDoctor className="text-3xl text-white" />
                                            </div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-xl font-bold text-base-content mb-2 group-hover:text-secondary transition-colors line-clamp-2">
                                                {professional.name}
                                            </h3>
                                            {professional.email && (
                                                <div className="flex items-center gap-2 text-sm text-base-content/70 mb-1.5">
                                                    <MdEmail className="text-base text-secondary/60 flex-shrink-0" />
                                                    <span className="truncate">
                                                        {professional.email}
                                                    </span>
                                                </div>
                                            )}
                                            {professional.phoneNumber && (
                                                <div className="flex items-center gap-2 text-sm text-base-content/70 mb-1.5">
                                                    <MdPhone className="text-base text-success/60 flex-shrink-0" />
                                                    <span className="font-medium">
                                                        {formatPhoneNumber(
                                                            professional.phoneNumber
                                                        )}
                                                    </span>
                                                </div>
                                            )}
                                            {professional.formation && (
                                                <div className="flex items-center gap-2 text-sm text-base-content/70">
                                                    <MdMedicalServices className="text-base text-info/60 flex-shrink-0" />
                                                    <span className="truncate">
                                                        {professional.formation}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-shrink-0">
                                            <div className="w-8 h-8 rounded-lg bg-base-200/50 group-hover:bg-secondary/10 flex items-center justify-center transition-colors">
                                                <MdArrowForward className="text-base-content/40 group-hover:text-secondary transition-colors" />
                                            </div>
                                        </div>
                                    </div>

                                    {professional.specialties &&
                                        professional.specialties.length > 0 && (
                                            <div className="mb-4">
                                                <div className="flex flex-wrap gap-1.5">
                                                    {professional.specialties
                                                        .slice(0, 3)
                                                        .map(specialty => (
                                                            <span
                                                                key={
                                                                    specialty.id
                                                                }
                                                                className="badge badge-sm badge-secondary badge-outline"
                                                            >
                                                                {
                                                                    specialty
                                                                        .specialty
                                                                        .name
                                                                }
                                                            </span>
                                                        ))}
                                                    {professional.specialties
                                                        .length > 3 && (
                                                        <span className="badge badge-sm badge-ghost">
                                                            +
                                                            {professional
                                                                .specialties
                                                                .length - 3}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                    {professional.partner && (
                                        <div className="flex items-center gap-2 text-sm text-base-content/70 mb-4 px-3 py-2 bg-warning/5 rounded-lg border border-warning/20">
                                            <MdHandshake className="text-base text-warning/70 flex-shrink-0" />
                                            <span className="truncate font-medium">
                                                {professional.partner
                                                    .tradingName ||
                                                    professional.partner.name}
                                            </span>
                                        </div>
                                    )}

                                    {(professional.city ||
                                        professional.state) && (
                                        <div className="flex items-center gap-2 text-sm text-base-content/60 mb-4 px-3 py-2 bg-base-200/30 rounded-lg">
                                            <MdLocationOn className="text-base text-info/60 flex-shrink-0" />
                                            <span className="truncate">
                                                {[
                                                    professional.city,
                                                    professional.state,
                                                ]
                                                    .filter(Boolean)
                                                    .join(', ')}
                                            </span>
                                        </div>
                                    )}

                                    <div className="pt-4 border-t border-base-300/50">
                                        <div className="flex items-center justify-between gap-4">
                                            <div className="text-xs text-base-content/50">
                                                {format(
                                                    new Date(
                                                        professional.createdAt
                                                    ),
                                                    'dd/MM/yyyy',
                                                    { locale: ptBR }
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {total > limit && (
                        <PaginationControls
                            page={page}
                            limit={limit}
                            total={total}
                            loading={loading}
                            onPageChange={setPage}
                            label="profissional"
                        />
                    )}
                </>
            )}
            <ImportDataModal
                isOpen={showImportModal}
                onClose={() => setShowImportModal(false)}
                module={ShareableModule.PROFESSIONAL}
                moduleLabel="Profissionais"
                fetchPaginated={async (companyId, params) =>
                    getProfessionalsByOrganization(companyId, params)
                }
                renderItem={(item: Professional) => (
                    <div>
                        <p className="text-sm font-medium truncate">
                            {item.name}
                        </p>
                        <p className="text-xs text-base-content/60 truncate">
                            {[
                                item.formation,
                                item.registrationNumber
                                    ? `${item.councilName || 'CRM'} ${item.registrationNumber}`
                                    : null,
                            ]
                                .filter(Boolean)
                                .join(' - ') || 'Sem informação'}
                        </p>
                    </div>
                )}
                getItemId={(item: Professional) => item.id}
                getItemName={(item: Professional) => item.name}
                onImportComplete={reload}
            />
        </div>
    )
}
